import Image from 'next/image';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import React, { useEffect, useState } from 'react';
import { FaInstagram, FaTiktok, FaXTwitter, FaYoutube } from 'react-icons/fa6';

import RelatedGuides from './RelatedGuides';

import GoogleStartupsLogo from '~/svg/Google_for_Startups_logo.svg';

const navigation = {
  solutions: [
    { name: 'Chat with AI', href: '/marky' },
    { name: 'AI Images', href: '/ai-image-generator' },
    { name: 'AI Videos', href: '/ai-video-generator' },
    { name: 'AI Video Upscaler', href: '/ai-video-upscaler' },
    { name: 'AI Video Extender', href: '/ai-video-extender' },
    { name: 'Edit Video with AI', href: '/edit-video-with-ai' },
    { name: 'Bots', href: '/chatbots' },
    { name: 'AI Transcription', href: '/ai-transcription' },
    { name: 'AI Text-to-Speech', href: '/text-to-speech' },
    { name: 'AI Voice Changer', href: '/voice-changer' },
    { name: 'AI Music Generator', href: '/ai-music-generator' },
    { name: 'AI Audio Generator', href: '/ai-audio-generator' },
    { name: 'AI Sound Effects Generator', href: '/ai-sound-effects-generator' },
    { name: 'AI Image to Animation', href: '/ai-animate-image' },
    { name: 'AI Photo Studio', href: '/ai-photo-generator' },
    { name: 'Professional Headshots', href: '/ai-headshot-generator' },
    { name: 'AI Interior Designer', href: '/ai-interior-design-generator' },
    { name: 'AI Talking Videos', href: '/ai-video-generator/talking' },
    { name: 'Templates', href: '/templates' },
    { name: 'Workflows', href: '/ai-workflows' },
    { name: 'Plugins', href: '/plugins' },
    { name: 'B2B Solutions', href: '/b2b' },
    { name: 'Free AI Tools', href: '/free-tools' },
  ],
  support: [
    { name: 'Pricing', href: '/pricing' },
    {
      name: 'API Documentation',
      href: 'https://easy-peasy.ai/docs/developers/overview',
      target: '_blank',
    },
    {
      name: 'Zapier Integration',
      href: 'https://zapier.com/apps/easy-peasyai/integrations',
      target: '_blank',
    },
    { name: 'Contact Us', href: '/contact' },
    { name: 'FAQ', href: '/#faq' },
    // Enterprise
    { name: 'Enterprise', href: '/b2b' },
  ],
  company: [
    { name: 'Mobile App', href: '/mobile-app' },
    {
      name: 'Reviews',
      href: 'https://www.trustpilot.com/review/easy-peasy.ai',
      target: '_blank',
    },
    { name: 'Blog', href: '/blog' },
    { name: 'Docs', href: '/docs' },
    { name: "What's New", href: '/changelog' },
    {
      name: 'Affiliate Program',
      href: '/affiliates',
    },
    {
      name: 'Creator Program',
      href: '/creators',
    },
    {
      name: 'AI Workshops',
      href: '/ai-workshops',
    },
    {
      name: 'About Us',
      href: '/about-us',
    },
    // { name: 'Press', href: '#' },
    // { name: 'Partners', href: '#' },
  ],
  legal: [
    { name: 'Privacy Policy', href: '/privacy-policy' },
    { name: 'Terms of Use', href: '/terms-of-use' },
    {
      name: 'Trust Center',
      href: 'https://easypeasyai.trust.site/',
      target: '_blank',
    },
  ],
  tools: [
    {
      name: 'Performance Review Generator',
      href: '/templates/performance-review-generator',
    },
    {
      name: 'Discussion Board Response Generator',
      href: '/templates/discussion-board-response-generator',
    },
    {
      name: 'Interview Answers Generator',
      href: '/templates/interview-answers-generator',
    },
    {
      name: 'Progress Note Generator',
      href: '/templates/progress-note-generator',
    },
    {
      name: 'SMART Goals Generator',
      href: '/templates/smart-goal-generator',
    },
    {
      name: 'Professional Bio Maker',
      href: '/templates/professional-bio-generator',
    },
    {
      name: 'Speech Writer',
      href: '/templates/speech-writer',
    },
    {
      name: 'Company Profile Generator',
      href: '/templates/company-profile-generator',
    },
    {
      name: 'Business Plan Generator',
      href: '/templates/business-plan-generator',
    },
    {
      name: 'Study Guide Maker',
      href: '/templates/study-guide-maker',
    },
    {
      name: 'Sermon Maker',
      href: '/templates/sermon-generator',
    },
    {
      name: 'Real Estate Listing Generator',
      href: '/templates/real-estate-listing-generator',
    },
    // Accent Generator
    {
      name: 'Australian Accent Generator',
      href: '/text-to-speech/australian',
    },
    {
      name: 'New Zealand Accent Generator',
      href: '/text-to-speech/new-zealand',
    },
    {
      name: 'Irish Accent Generator',
      href: '/text-to-speech/irish',
    },
    {
      name: 'Scottish Accent Generator',
      href: '/text-to-speech/scottish',
    },
    {
      name: 'South African Accent Generator',
      href: '/text-to-speech/south-african',
    },
  ],
};

export default function NewFooter({
  variant,
}: {
  variant?: 'default' | 'dark';
}) {
  const { t } = useLocale('footer');
  const isDark = variant === 'dark';
  const [theme, setTheme] = useState(isDark ? 'dark' : 'light');

  // set theme on mount
  useEffect(() => {
    if (isDark) return;
    const theme = localStorage.getItem('theme');
    if (
      theme === 'dark' ||
      ((!('theme' in localStorage) || theme === 'system') &&
        window.matchMedia('(prefers-color-scheme: dark)').matches)
    ) {
      setTheme('dark');
    }
  }, [isDark]);

  return (
    <>
      {/* Tool pages link to the blog tutorials that teach them. */}
      <RelatedGuides dark={isDark} />
      <footer
        className={isDark ? 'dark bg-slate-900' : 'bg-white dark:bg-slate-900'}
        aria-labelledby='footer-heading'
      >
        <h2 id='footer-heading' className='sr-only'>
          Footer
        </h2>
        <div className='mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16'>
          <div className='xl:grid xl:grid-cols-3 xl:gap-8'>
            <div className='grid grid-rows-[auto_1fr_auto] gap-8 xl:col-span-1'>
              <div className='space-y-8'>
                <Link href='/'>
                  <Image
                    src='/images/marky.png'
                    width='250'
                    height='36'
                    alt='Easy-Peasy.ai logo'
                  />
                </Link>
                <p className='text-base text-gray-500 dark:text-slate-400'>
                  {t('Best AI Content Generator & Copywriting Assistant')}
                </p>
                <div className='flex space-x-6'>
                  <Link
                    className='text-gray-500 hover:text-gray-300'
                    href='https://www.facebook.com/groups/1565143487285854'
                    target='_blank'
                    rel='noreferrer'
                  >
                    <svg
                      fill='currentColor'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      className='h-5 w-5'
                      viewBox='0 0 24 24'
                    >
                      <path d='M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z'></path>
                    </svg>
                  </Link>
                  <Link
                    className='ml-3 text-gray-500 hover:text-gray-300'
                    href='https://x.com/easy_peasy_ai'
                    target='_blank'
                    rel='noreferrer'
                  >
                    <FaXTwitter className='h-5 w-5' />
                  </Link>
                  <Link
                    className='ml-3 text-gray-500 hover:text-gray-300'
                    href='https://www.youtube.com/@easy-peasy-ai'
                    target='_blank'
                    rel='noreferrer'
                  >
                    <FaYoutube className='h-5 w-5' />
                  </Link>
                  <Link
                    className='ml-3 text-gray-500 hover:text-gray-300'
                    href='https://www.instagram.com/easy_peasy.ai/'
                    target='_blank'
                    rel='noreferrer'
                  >
                    <FaInstagram className='h-5 w-5' />
                  </Link>
                  <Link
                    className='ml-3 text-gray-500 hover:text-gray-300'
                    href='https://www.tiktok.com/@easy_peasy_ai'
                    target='_blank'
                    rel='noreferrer'
                  >
                    <FaTiktok className='h-5 w-5' />
                  </Link>
                  <Link
                    className='ml-3 text-gray-500 hover:text-gray-300'
                    href='https://www.linkedin.com/company/easy-peasy-ai/ '
                    target='_blank'
                    rel='noreferrer'
                  >
                    <svg
                      fill='currentColor'
                      stroke='currentColor'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='0'
                      className='h-5 w-5'
                      viewBox='0 0 24 24'
                    >
                      <path
                        stroke='none'
                        d='M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z'
                      ></path>
                      <circle cx='4' cy='4' r='2' stroke='none'></circle>
                    </svg>
                  </Link>
                </div>

                <div>
                  {theme === 'dark' ? (
                    <div className='flex items-center space-x-4'>
                      <Image
                        src='/images/sme500-dark.png'
                        width={120}
                        height={120}
                        alt='SME 500'
                        className='-ml-2'
                        title='SME 500 Singapore Award Winner'
                      />
                      <Image
                        src='/images/gsa-dark.png'
                        width={120}
                        height={120}
                        alt='Best WEB 3.0 and AI Startup'
                        title='Best WEB 3.0 and AI Startup'
                      />
                      <Image
                        src='/images/nvidia-inception-program-badge.png'
                        width={120}
                        height={44}
                        className='h-[44px]'
                        alt='NVIDIA Inception Program'
                        title='NVIDIA Inception Program'
                      />
                    </div>
                  ) : (
                    <div className='flex flex-wrap items-center gap-x-4'>
                      <Image
                        src='/images/nvidia-inception-program-badge.png'
                        width={120}
                        height={44}
                        className='h-[44px]'
                        alt='NVIDIA Inception Program'
                        title='NVIDIA Inception Program'
                      />
                      <Image
                        src='/images/gsa.png'
                        width={120}
                        height={120}
                        alt='Best WEB 3.0 and AI Startup'
                        title='Best WEB 3.0 and AI Startup'
                      />
                      <Image
                        src='/images/sme500.png'
                        width={120}
                        height={120}
                        alt='SME 500'
                        title='SME 500 Singapore Award Winner'
                      />
                    </div>
                  )}
                  <div className='flex items-center'>
                    <GoogleStartupsLogo className='w-64 h-16 text-gray-800 dark:text-gray-300' />
                  </div>
                </div>
              </div>

              <div className='flex flex-col space-y-4 self-end'>
                <div className='flex items-center space-x-4'>
                  <Image
                    src='/images/soc2.png'
                    width={40}
                    height={40}
                    alt='SOC2 Certified'
                    title='SOC2 Certified'
                  />
                  <div className='text-sm font-semibold text-gray-500 dark:text-slate-400'>
                    {t('Enterprise-grade security')}
                    <br />
                    {t('SOC2')}
                  </div>
                </div>
                <div className='flex items-center space-x-4'>
                  <Image
                    src='/images/iso-27001.png'
                    width={40}
                    height={40}
                    alt='ISO 27001 Certified'
                    title='ISO 27001 Certified'
                  />
                  <div className='text-sm font-semibold text-gray-500 dark:text-slate-400'>
                    ISO 27001
                  </div>
                </div>

                <div className='flex items-center space-x-4'>
                  <Image
                    src='/images/hipaa.png'
                    width={40}
                    height={40}
                    alt='HIPAA Compliant'
                    title='HIPAA Compliant'
                  />
                  <div className='text-sm font-semibold text-gray-500 dark:text-slate-400'>
                    {t('HIPAA-compliant')}
                  </div>
                </div>
                <div className='flex items-center space-x-4'>
                  <Image
                    src='/images/gdpr.png'
                    width={40}
                    height={40}
                    alt='GDPR Compliant'
                    title='GDPR Compliant'
                  />
                  <div className='text-sm font-semibold text-gray-500 dark:text-slate-400'>
                    {t('GDPR-compliant')}
                  </div>
                </div>
              </div>
            </div>
            <div className='mt-12 grid grid-cols-2 gap-8 xl:col-span-2 xl:mt-0'>
              <div className='md:grid md:grid-cols-2 md:gap-8'>
                <div>
                  <h3 className='text-base font-medium text-gray-900 dark:text-slate-300'>
                    {t('Solutions')}
                  </h3>
                  <ul role='list' className='mt-4 space-y-4'>
                    {navigation.solutions.map((item) => (
                      <li key={t(item.name)}>
                        <Link
                          href={item.href}
                          className='text-base text-gray-500 hover:text-gray-900 dark:hover:text-slate-400'
                        >
                          {t(item.name)}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className='mt-12 md:mt-0'>
                  <h3 className='text-base font-medium text-gray-900 dark:text-slate-300'>
                    {t('Tools')}
                  </h3>
                  <ul role='list' className='mt-4 space-y-4'>
                    {navigation.tools.map((item) => (
                      <li key={t(item.name)}>
                        <Link
                          href={item.href}
                          className='text-base text-gray-500 hover:text-gray-900 dark:hover:text-slate-400'
                        >
                          {t(item.name).replace('Generator', '')}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className='md:grid md:grid-cols-2 md:gap-8'>
                <div>
                  <h3 className='text-base font-medium text-gray-900 dark:text-slate-300'>
                    {t('Support')}
                  </h3>
                  <ul role='list' className='mt-4 space-y-4'>
                    {navigation.support.map((item) => (
                      <li key={t(item.name)}>
                        <Link
                          href={item.href}
                          className='text-base text-gray-500 hover:text-gray-900 dark:hover:text-slate-400'
                          target={item.target || '_self'}
                        >
                          {t(item.name)}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <h3 className='mt-8 text-base font-medium text-gray-900 dark:text-slate-300'>
                    {t('Legal')}
                  </h3>
                  <ul role='list' className='mt-4 space-y-4'>
                    {navigation.legal.map((item) => (
                      <li key={t(item.name)}>
                        <Link
                          href={item.href}
                          className='text-base text-gray-500 hover:text-gray-900 dark:hover:text-slate-400'
                          target={item.target || '_self'}
                        >
                          {t(item.name)}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className='mt-12 md:mt-0'>
                  <h3 className='text-base font-medium text-gray-900 dark:text-slate-300'>
                    {t('Company')}
                  </h3>
                  <ul role='list' className='mt-4 space-y-4'>
                    {navigation.company.map((item) => (
                      <li key={t(item.name)}>
                        <Link
                          href={item.href}
                          className='text-base text-gray-500 hover:text-gray-900 dark:hover:text-slate-400'
                          target={item.target || '_self'}
                        >
                          {t(item.name)}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className='mt-12 border-t border-gray-200 pt-8 dark:border-white/10'>
            <p className='text-base text-gray-500 xl:text-center'>
              &copy; 2025 Easy-Peasy.AI Pte. Ltd. (202330445W) 18 Cross St,
              #02-101 Cross Street Exchange, Singapore 048423
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}
